import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { Icon } from './Icon';

interface StateProps {
  title: string;
  description?: ReactNode;
  action?: { label: string; onClick: () => void; icon?: string };
  icon?: string;
  minHeight?: number;
}

/** Design empty state: 56px icon circle, title, text, outlined action. */
export function EmptyState({ title, description, action, icon = 'inbox', minHeight = 240 }: StateProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        textAlign: 'center',
        minHeight,
        p: 3,
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 28,
          bgcolor: 'surface.container',
          color: 'text.tertiary',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={icon} size={28} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
        <Typography variant="subtitle1">{title}</Typography>
        {description && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', maxWidth: 340, textWrap: 'pretty' }}>
            {description}
          </Typography>
        )}
      </Box>
      {action && (
        <Button
          variant="outlined"
          size="small"
          onClick={action.onClick}
          startIcon={action.icon ? <Icon name={action.icon} size={18} /> : undefined}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
}

/** Design error state: err-container circle, cloud_off, primary "retry" action. */
export function ErrorState({ title, description, action, icon = 'cloud_off', minHeight = 240 }: StateProps) {
  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        textAlign: 'center',
        minHeight,
        p: 3,
      }}
    >
      <Box
        sx={(t) => ({
          width: 56,
          height: 56,
          borderRadius: 28,
          bgcolor: t.vars.palette.error.container,
          color: t.vars.palette.error.main,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        <Icon name={icon} size={28} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
        <Typography variant="subtitle1">{title}</Typography>
        {description && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', maxWidth: 340, textWrap: 'pretty' }}>
            {description}
          </Typography>
        )}
      </Box>
      {action && (
        <Button
          size="small"
          onClick={action.onClick}
          startIcon={<Icon name={action.icon ?? 'refresh'} size={18} />}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
}

/** Loading rows: avatar dot, text bar, trailing chip. */
export function SkeletonRows({
  rows = 4,
  widths = ['46%', '38%', '52%', '30%'],
}: {
  rows?: number;
  widths?: string[];
}) {
  return (
    <Box aria-busy>
      {Array.from({ length: rows }, (_, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            height: 40,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton
            variant="rounded"
            height={10}
            sx={{ width: widths[i % widths.length], borderRadius: '5px' }}
          />
          <Box sx={{ flex: 1 }} />
          <Skeleton variant="rounded" width={56} height={18} sx={{ borderRadius: '6px' }} />
        </Box>
      ))}
    </Box>
  );
}
